import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Switch, FormControlLabel, Tooltip, Avatar,
  FormControl, FormLabel, FormGroup, Checkbox,
} from '@mui/material';
import { Add, Edit, Delete, AccessTime, Work, Schedule, Coffee, Today } from '@mui/icons-material';
import { shiftsAPI } from '../services/api';
import Swal from 'sweetalert2';

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [formData, setFormData] = useState({
    shift_name: '', shift_code: '', start_time: '09:00', end_time: '18:00',
    first_break_after_minutes: 45, short_break_duration: 10, long_break_duration: 45,
    max_short_breaks: 4, max_long_breaks: 1, late_threshold_minutes: 30,
    early_logout_threshold_minutes: 30, expected_work_hours: 480, is_night_shift: false,
    description: '', color: '#D4AF37',
    weekend_off: true, first_saturday_work: false,
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    full_day_hours: 8.0, half_day_hours: 4.0,
  });

  useEffect(() => { loadShifts(); }, []);

  const loadShifts = async () => {
    try {
      const response = await shiftsAPI.getAllAdmin();
      if (response.data.success) {
        const processedShifts = response.data.data.shifts.map(shift => ({
          ...shift,
          working_days: Array.isArray(shift.working_days)
            ? shift.working_days
            : (shift.working_days ? JSON.parse(shift.working_days) : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']),
          weekend_off: Boolean(shift.weekend_off),
          first_saturday_work: Boolean(shift.first_saturday_work)
        }));
        setShifts(processedShifts);
      }
    } catch (error) { console.error('Error loading shifts:', error); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      const dataToSend = {
        ...formData,
        working_days: formData.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      };

      if (editShift) {
        await shiftsAPI.update(editShift.id, dataToSend);
      } else {
        await shiftsAPI.create(dataToSend);
      }
      setShowDialog(false);
      loadShifts();
    } catch (error) {
      console.error('Error saving shift:', error);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Shift',
      text: 'Are you sure you want to delete this shift?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
      background: 'rgb(10, 25, 41)',
      color: '#D4AF37',
    });
    if (result.isConfirmed) {
      try {
        await shiftsAPI.delete(id);
        loadShifts();
        Swal.fire({
          title: 'Deleted!',
          text: 'Shift has been deleted.',
          icon: 'success',
          background: 'rgb(10, 25, 41)',
          color: '#D4AF37',
          confirmButtonColor: '#D4AF37',
        });
      } catch (error) { console.error('Error:', error); }
    }
  };

  const openEdit = (shift) => {
    setEditShift(shift);
    const shiftData = {
      ...shift,
      working_days: shift.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      weekend_off: shift.weekend_off !== undefined ? Boolean(shift.weekend_off) : true,
      first_saturday_work: shift.first_saturday_work !== undefined ? Boolean(shift.first_saturday_work) : false,
      full_day_hours: shift.full_day_hours || 8.0,
      half_day_hours: shift.half_day_hours || 4.0,
    };
    setFormData(shiftData);
    setShowDialog(true);
  };

  const openNew = () => {
    setEditShift(null);
    setFormData({
      shift_name: '', shift_code: '', start_time: '09:00', end_time: '18:00',
      first_break_after_minutes: 45, short_break_duration: 10, long_break_duration: 45,
      max_short_breaks: 4, max_long_breaks: 1, late_threshold_minutes: 30,
      early_logout_threshold_minutes: 30, expected_work_hours: 480, is_night_shift: false,
      description: '', color: '#D4AF37',
      weekend_off: true, first_saturday_work: false,
      working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      full_day_hours: 8.0, half_day_hours: 4.0,
    });
    setShowDialog(true);
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time.substring(0, 5);
  };

  const formatWorkHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            Shift Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={openNew}
            sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
          >
            Add Shift
          </Button>
        </Box>

        {/* Shifts Table */}
        <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Shift Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Breaks</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Work Hours</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', fontSize: '0.9rem' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shifts.map((shift) => (
                    <TableRow key={shift.id} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: shift.color || '#D4AF37',
                            boxShadow: '0 0 4px rgba(212,175,55,0.5)'
                          }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                            {shift.shift_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={shift.shift_code} 
                          sx={{ 
                            bgcolor: 'rgba(212,175,55,0.2)', 
                            color: '#D4AF37',
                            border: '1px solid #D4AF37',
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime sx={{ fontSize: 14, color: '#D4AF37' }} />
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            size="small" 
                            icon={<Coffee sx={{ fontSize: 14 }} />}
                            label={`${shift.max_short_breaks} short`}
                            sx={{ 
                              bgcolor: 'rgba(255, 152, 0, 0.2)', 
                              color: '#FFB74D',
                              border: '1px solid #FFB74D',
                              height: 24
                            }} 
                          />
                          <Chip 
                            size="small" 
                            label={`${shift.max_long_breaks} long`}
                            sx={{ 
                              bgcolor: 'rgba(33, 150, 243, 0.2)', 
                              color: '#90CAF9',
                              border: '1px solid #90CAF9',
                              height: 24
                            }} 
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#D4AF37', fontWeight: 500 }}>
                          {formatWorkHours(shift.expected_work_hours || 480)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={shift.is_active ? 'Active' : 'Inactive'} 
                          sx={{
                            bgcolor: shift.is_active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                            color: shift.is_active ? '#81C784' : '#B4C4A8',
                            border: `1px solid ${shift.is_active ? '#81C784' : '#B4C4A8'}`
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Shift">
                          <IconButton 
                            size="small" 
                            onClick={() => openEdit(shift)} 
                            sx={{ color: '#D4AF37', mr: 1, '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Shift">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDelete(shift.id)} 
                            sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {shifts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#B4C4A8' }}>No shifts found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add/Edit Shift Dialog */}
        <Dialog 
          open={showDialog} 
          onClose={() => setShowDialog(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            {editShift ? 'Edit Shift' : 'Add New Shift'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#D4AF37', fontWeight: 600, mb: 1 }}>
                  <Work sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Shift Name" 
                  value={formData.shift_name} 
                  onChange={(e) => setFormData({ ...formData, shift_name: e.target.value })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Shift Code" 
                  value={formData.shift_code} 
                  onChange={(e) => setFormData({ ...formData, shift_code: e.target.value })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Start Time" 
                  type="time" 
                  value={formData.start_time} 
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} 
                  InputLabelProps={{ shrink: true, sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="End Time" 
                  type="time" 
                  value={formData.end_time} 
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} 
                  InputLabelProps={{ shrink: true, sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  label="Description" 
                  multiline 
                  rows={2} 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>

              {/* Rest of the dialog content remains the same */}
              {/* Break Configuration Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#D4AF37', fontWeight: 600, mt: 2, mb: 1 }}>
                  <Coffee sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  Break Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="First Break After (minutes)" 
                  type="number" 
                  value={formData.first_break_after_minutes} 
                  onChange={(e) => setFormData({ ...formData, first_break_after_minutes: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Short Break Duration (minutes)" 
                  type="number" 
                  value={formData.short_break_duration} 
                  onChange={(e) => setFormData({ ...formData, short_break_duration: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Long Break Duration (minutes)" 
                  type="number" 
                  value={formData.long_break_duration} 
                  onChange={(e) => setFormData({ ...formData, long_break_duration: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Max Short Breaks per Day" 
                  type="number" 
                  value={formData.max_short_breaks} 
                  onChange={(e) => setFormData({ ...formData, max_short_breaks: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Max Long Breaks per Day" 
                  type="number" 
                  value={formData.max_long_breaks} 
                  onChange={(e) => setFormData({ ...formData, max_long_breaks: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>

              {/* Working Days Configuration Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#D4AF37', fontWeight: 600, mt: 2, mb: 1 }}>
                  <Today sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  Working Days Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.weekend_off !== undefined ? Boolean(formData.weekend_off) : true}
                      onChange={(e) => {
                        const newValue = Boolean(e.target.checked);
                        setFormData(prev => ({ ...prev, weekend_off: newValue }));
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Weekend Off (Saturday & Sunday)</Typography>}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.first_saturday_work)}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          first_saturday_work: !prev.first_saturday_work
                        }));
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>First Saturday Working Day</Typography>}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" sx={{ mt: 1 }}>
                  <FormLabel component="legend" sx={{ color: '#D4AF37', fontWeight: 500 }}>
                    Select Working Days
                  </FormLabel>
                  <FormGroup row sx={{ mt: 1 }}>
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={formData.working_days?.includes(day) || false}
                            onChange={(e) => {
                              const currentDays = Array.isArray(formData.working_days) ? formData.working_days : [];
                              let updatedDays;
                              if (e.target.checked) {
                                updatedDays = [...currentDays, day];
                              } else {
                                updatedDays = currentDays.filter(d => d !== day);
                              }
                              setFormData(prev => ({ ...prev, working_days: updatedDays }));
                            }}
                            sx={{
                              '& .MuiCheckbox-root': { color: '#D4AF37' },
                              '& .Mui-checked': { color: '#D4AF37' },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: '#FFFFFF', textTransform: 'capitalize' }}>
                            {day}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>

              {/* Working Hours Configuration Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#D4AF37', fontWeight: 600, mt: 2, mb: 1 }}>
                  <Schedule sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  Working Hours Configuration
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Full Day Working Hours"
                  value={formData.full_day_hours}
                  onChange={(e) => setFormData({ ...formData, full_day_hours: parseFloat(e.target.value) || 8.0 })}
                  inputProps={{ min: 1, max: 24, step: 0.5 }}
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Half Day Working Hours"
                  value={formData.half_day_hours}
                  onChange={(e) => setFormData({ ...formData, half_day_hours: parseFloat(e.target.value) || 4.0 })}
                  inputProps={{ min: 0.5, max: 12, step: 0.5 }}
                  InputLabelProps={{ sx: { color: '#D4AF37' } }}
                  sx={{
                    '& .MuiInputLabel-root': { color: '#D4AF37' },
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#D4AF37' },
                      '&:hover fieldset': { borderColor: '#D4AF37' },
                      '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                    }
                  }}
                />
              </Grid>

              {/* Attendance Rules Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#D4AF37', fontWeight: 600, mt: 2, mb: 1 }}>
                  <Schedule sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  Attendance Rules
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Late Threshold (minutes)" 
                  type="number" 
                  value={formData.late_threshold_minutes} 
                  onChange={(e) => setFormData({ ...formData, late_threshold_minutes: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Early Logout Threshold (minutes)" 
                  type="number" 
                  value={formData.early_logout_threshold_minutes} 
                  onChange={(e) => setFormData({ ...formData, early_logout_threshold_minutes: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Expected Work Hours (minutes)" 
                  type="number" 
                  value={formData.expected_work_hours} 
                  onChange={(e) => setFormData({ ...formData, expected_work_hours: parseInt(e.target.value) })}
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                  helperText={`= ${Math.floor(formData.expected_work_hours / 60)}h ${formData.expected_work_hours % 60}m`}
                  FormHelperTextProps={{ sx: { color: '#B4C4A8' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Shift Color" 
                  type="color" 
                  value={formData.color} 
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  InputLabelProps={{ shrink: true, sx: { color: '#B4C4A8' } }}
                  InputProps={{ sx: { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={formData.is_night_shift} 
                      onChange={(e) => setFormData({ ...formData, is_night_shift: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#D4AF37' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D4AF37' },
                      }}
                    />
                  } 
                  label="Night Shift"
                  sx={{ color: '#B4C4A8' }}
                />
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={formData.is_active !== undefined ? formData.is_active : true} 
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#81C784' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#81C784' },
                      }}
                    />
                  } 
                  label="Active"
                  sx={{ color: '#B4C4A8', ml: 2 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
            >
              {editShift ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Shifts;